/**
 * Which modules a company has on, in one place.
 *
 * Most modules are a commercial entitlement (the plan includes them) that an admin can then switch
 * off. The `DEFAULT_ON_MODULE_KEYS` below are different: Help desk, Training, Engage, Offboarding,
 * Travel and Connected applications were never part of a plan and have always been on for everyone, so they are on unless an
 * admin switched them off. They are NOT stored as entitlements; the cache records only what was
 * switched off (`disabledModules`), so no existing company and no new signup loses anything when
 * these keys were introduced.
 *
 * `effectiveModules` is the one definition. The API gate, `/me` (and so the menu, Home and the route
 * guard) and the Settings > Modules screen all use it. Pure and RN-safe.
 */

import { MODULE_KEYS, type ModuleKey } from '../enums.js';

/** Areas that are on until an admin switches them off; never part of a plan. */
export const DEFAULT_ON_MODULE_KEYS = [
  'helpdesk',
  'training',
  'engage',
  'offboarding',
  'travel',
  'app_integrations',
] as const satisfies readonly ModuleKey[];
export type DefaultOnModuleKey = (typeof DEFAULT_ON_MODULE_KEYS)[number];

export function isDefaultOnModule(key: string): key is DefaultOnModuleKey {
  return (DEFAULT_ON_MODULE_KEYS as readonly string[]).includes(key);
}

/** What `Organization.settings` holds about modules (a cache written by the API). */
export interface ModuleSettings {
  /** Entitled AND not switched off, for plan-based modules. */
  modules?: readonly string[] | null;
  /** Every module an admin switched off, plan-based or default-on. */
  disabledModules?: readonly string[] | null;
}

/**
 * The module(s) a contract router belongs to, for the default-on areas and Engage's announcements.
 * The copilot uses it to stop offering tools for an area that is switched off. The other modules are
 * not listed: their routes already refuse a switched-off module at the API, which is what the copilot
 * would run into anyway.
 */
export function modulesForContractKey(key: string): ModuleKey[] {
  if (key === 'helpdesk') return ['helpdesk'];
  if (key === 'learningAssignments' || key.startsWith('training')) return ['training'];
  if (key === 'offboarding') return ['offboarding'];
  if (key === 'surveys' || key === 'celebrations' || key === 'engagementInsights') return ['engage'];
  if (key === 'announcements') return ['engage', 'chat'];
  if (key.startsWith('travel')) return ['travel'];
  if (key === 'appIntegrations') return ['app_integrations'];
  return [];
}

/** The modules this company can use now. A key switched off is never in it. */
export function effectiveModules(settings: ModuleSettings | null | undefined): ModuleKey[] {
  const known = new Set<string>(MODULE_KEYS);
  const off = new Set(settings?.disabledModules ?? []);
  const on = new Set<ModuleKey>();
  for (const key of settings?.modules ?? []) {
    if (known.has(key) && !off.has(key)) on.add(key as ModuleKey);
  }
  for (const key of DEFAULT_ON_MODULE_KEYS) {
    if (!off.has(key)) on.add(key);
  }
  return [...on];
}
